// Client-side view of the tool-policy modes (PLAN-agent-v2 #5/#6). The SERVER decides
// `requires_confirmation` per call (mode × risk) and sends it in the batch; the client trusts
// that flag but re-derives the same matrix so the UI can explain a mode and stay safe if a
// payload is missing the flag.
import type { ClientToolCall, Mode, RiskClass } from '../../types/agent';

// Default `auto` (product owner, 2026-09-10): writes run without approval cards, destructive actions still ask (#6).
export const DEFAULT_MODE: Mode = 'auto';

/** Same matrix as the backend `policy.py` (#5): which risk classes are confirmed per mode. */
export function requiresConfirmation(mode: Mode, risk: RiskClass): boolean {
  switch (mode) {
    case 'plan': // read-only: the server answers its writes `PlanMode` and never sends them in a batch
    case 'ask':
      return risk === 'write' || risk === 'destructive';
    case 'auto':
      return risk === 'destructive';
    case 'full_auto':
      return false;
    default:
      return true;
  }
}

/** Effective flag for a call: the server flag wins; fall back to the local matrix. */
export function needsApproval(call: ClientToolCall, mode: Mode): boolean {
  // Domain #36: a protected sheet is confirmed in every mode, whatever the server flag says.
  if (call.protected) return true;
  if (typeof call.requires_confirmation === 'boolean') return call.requires_confirmation;
  return requiresConfirmation(mode, call.risk);
}

/** Split a batch into calls that wait for the user and calls that run immediately. */
export function partitionBatch(calls: ClientToolCall[], mode: Mode): { toConfirm: ClientToolCall[]; immediate: ClientToolCall[] } {
  const toConfirm: ClientToolCall[] = [];
  const immediate: ClientToolCall[] = [];
  for (const c of calls) (needsApproval(c, mode) ? toConfirm : immediate).push(c);
  return { toConfirm, immediate };
}

export function isMode(value: unknown): value is Mode {
  return value === 'plan' || value === 'ask' || value === 'auto' || value === 'full_auto';
}
